import React from "react";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";
import type { Source } from "../types";

interface EvaluationSourcesProps {
  sources: Source[];
  copiedSourceId: string | null;
  onSourceClick: (type: string, id: string) => void;
  onCopyId: (id: string) => void;
}

/**
 * Groups sources by type and renders them with clickable IDs and copy buttons.
 */
export function EvaluationSources({
  sources,
  copiedSourceId,
  onSourceClick,
  onCopyId,
}: EvaluationSourcesProps) {
  const byType = sources.reduce<Record<string, string[]>>((acc, s) => {
    (acc[s.type] ??= []).push(s.id);
    return acc;
  }, {});

  return (
    <div className="mt-2 pt-2 border-t border-border">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Sources
      </h4>
      <div className="space-y-2">
        {Object.entries(byType).map(([type, ids]) => (
          <div key={type}>
            <span className="text-xs font-medium text-muted-foreground capitalize">
              {type}
            </span>
            <div className="mt-1 flex flex-wrap gap-1">
              {ids.map((id) => (
                <span key={id} className="inline-flex items-center gap-1">
                  <button
                    onClick={() => onSourceClick(type, id)}
                    className="text-xs font-mono bg-muted text-foreground border border-border hover:bg-accent hover:text-accent-foreground px-2 py-0.5 rounded transition-colors text-left"
                    title={`View ${type} details`}
                  >
                    {id.slice(0, 8)}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(id);
                      onCopyId(id);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    title="Copy source ID"
                  >
                    {copiedSourceId === id ? (
                      <CheckIcon className="w-3 h-3 text-success" />
                    ) : (
                      <ClipboardIcon className="w-3 h-3" />
                    )}
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
